import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout, LoadingPage } from './components/Layout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import OrderOverviewPage from './pages/OrderOverviewPage';
import CompletedOrdersPage from './pages/CompletedOrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ProductionPage from './pages/ProductionPage';
import DealersPage from './pages/DealersPage';
import DealerDetailPage from './pages/DealerDetailPage';
import InventoryPage from './pages/InventoryPage';
import BomPage from './pages/BomPage';
import ReportsPage from './pages/ReportsPage';
import InventoryReportsPage from './pages/InventoryReportsPage';
import UserManagementPage from './pages/UserManagementPage';
import SalesPage from './pages/SalesPage';
import MarketingPage from './pages/MarketingPage';
import LeadDistributionPage from './pages/LeadDistributionPage';
import ShippingPage from './pages/ShippingPage';
import SettingsPage from './pages/SettingsPage';
import ManufacturingRulesPage from './pages/ManufacturingRulesPage';
import WarrantyRegistrationsPage from './pages/warranty/WarrantyRegistrationsPage';
import RmaQueuePage from './pages/warranty/RmaQueuePage';
import RmaHistoryPage from './pages/warranty/RmaHistoryPage';
import { HRDashboardPage } from './pages/hr/HRDashboardPage';
import { TrainingManagementPage } from './pages/hr/TrainingManagementPage';
import { DocumentsManagementPage } from './pages/hr/DocumentsManagementPage';
import { SeparationsPage } from './pages/hr/SeparationsPage';
import { SupportRequestsPage } from './pages/hr/SupportRequestsPage';
import { ReviewsPage } from './pages/hr/ReviewsPage';
import { RewardsManagementPage } from './pages/hr/RewardsManagementPage';
import { TimeManagementPage } from './pages/hr/TimeManagementPage';
import { CrosschexPage } from './pages/hr/CrosschexPage';
import { PayrollPage } from './pages/hr/PayrollPage';
import { PayrollReportsPage } from './pages/reports/PayrollReportsPage';
import UserDeletionPage from './pages/UserDeletionPage';
import InventoryGroupsPage from './pages/InventoryGroupsPage';
import ProductLinesPage from './pages/ProductLinesPage';
import BinsPage from './pages/BinsPage';
import LabelsPage from './pages/LabelsPage';
import PurchasingReportPage from './pages/PurchasingReportPage';
import FabricSettingsPage from './pages/FabricSettingsPage';
import DealerRewardsPage from './pages/DealerRewardsPage';
import SupportTicketsPage from './pages/SupportTicketsPage';
import SalesTaxNexusPage from './pages/SalesTaxNexusPage';
import TaxCollectedReportPage from './pages/TaxCollectedReportPage';
import TaxFilingsPage from './pages/TaxFilingsPage';
import DealerTrainingManagementPage from './pages/DealerTrainingManagementPage';
import DisciplinePage from './pages/DisciplinePage';
import InfractionTypesPage from './pages/InfractionTypesPage';
import BinTypesPage from './pages/BinTypesPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import AuditLogPage from './pages/AuditLogPage';
import SecurityPage from './pages/SecurityPage';
import WorkstationStepsPage from './pages/WorkstationStepsPage';
import ProductionFloorPage from './pages/ProductionFloorPage';
import { OperatingCostsPage } from './pages/OperatingCostsPage';
import { CostAnalyticsPage } from './pages/analytics/CostAnalyticsPage';
import CompanyCalendarPage from './pages/CompanyCalendarPage';
import PartsPullTemplatePage from './pages/PartsPullTemplatePage';
import BackordersPage from './pages/BackordersPage';
import SpecialOrdersPage from './pages/SpecialOrdersPage';
import WhatToOrderPage from './pages/WhatToOrderPage';

// Accounting Pages
import AccountingDashboard from './pages/accounting/AccountingDashboard';
import DealerApplicationsPage from './pages/accounting/DealerApplicationsPage';
import DealerApplicationDetailPage from './pages/accounting/DealerApplicationDetailPage';
import BillsListPage from './pages/accounting/BillsListPage';
import BillEntryPage from './pages/accounting/BillEntryPage';
import EmployeeTaxListPage from './pages/accounting/EmployeeTaxListPage';
import EmployeeTaxEditPage from './pages/accounting/EmployeeTaxEditPage';
import RevenueShippedPage from './pages/accounting/RevenueShippedPage';
import RevenuePaymentsPage from './pages/accounting/RevenuePaymentsPage';
import WriteOffsPage from './pages/accounting/WriteOffsPage';
import QuickBooksPage from './pages/accounting/QuickBooksPage';
import PaymentAuditPage from './pages/accounting/PaymentAuditPage';
import InvoicesAdjustedPage from './pages/accounting/InvoicesAdjustedPage';
import CreditsPage from './pages/accounting/CreditsPage';
import NexusReportPage from './pages/NexusReportPage';

function ProtectedRoute() {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Accounting-only users can access /accounting/*, /sales, /dealers, and /users paths
  const isAccountingOnly = user.allowed_portals?.includes('accounting') &&
                           !user.allowed_portals?.includes('factory');
  if (isAccountingOnly
    && !location.pathname.startsWith('/accounting')
    && location.pathname !== '/sales'
    && !location.pathname.startsWith('/dealers')
    && !location.pathname.startsWith('/users')
  ) {
    return <Navigate to="/accounting/dashboard" replace />;
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function PublicRoute() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (user) {
    // Route to appropriate dashboard based on user type
    const isAccountingOnly = user.allowed_portals?.includes('accounting') &&
                             !user.allowed_portals?.includes('factory');
    if (isAccountingOnly) {
      return <Navigate to="/accounting/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route element={<PublicRoute />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/order-overview" element={<OrderOverviewPage />} />
        <Route path="/orders/completed" element={<CompletedOrdersPage />} />
        <Route path="/orders/:id" element={<OrderDetailPage />} />
        <Route path="/production" element={<ProductionPage />} />
        <Route path="/dealers" element={<DealersPage />} />
        <Route path="/dealers/:id" element={<DealerDetailPage />} />
        <Route path="/dealers/rewards" element={<DealerRewardsPage />} />
        <Route path="/dealers/training" element={<DealerTrainingManagementPage />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/what-to-order" element={<WhatToOrderPage />} />
        <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="/bins" element={<BinsPage />} />
        <Route path="/labels" element={<LabelsPage />} />
        <Route path="/bom" element={<BomPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/inventory" element={<InventoryReportsPage />} />
        <Route path="/reports/payroll" element={<PayrollReportsPage />} />
        <Route path="/reports/purchasing" element={<PurchasingReportPage />} />
        <Route path="/reports/nexus" element={<NexusReportPage />} />
        <Route path="/users" element={<UserManagementPage />} />
        <Route path="/users/deletion" element={<UserDeletionPage />} />
        <Route path="/sales" element={<SalesPage />} />
        <Route path="/marketing" element={<MarketingPage />} />
        <Route path="/lead-distribution" element={<LeadDistributionPage />} />
        <Route path="/shipping" element={<ShippingPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/deductions" element={<SettingsPage />} />
        <Route path="/settings/manufacturing-rules" element={<ManufacturingRulesPage />} />
        <Route path="/settings/inventory-groups" element={<InventoryGroupsPage />} />
        <Route path="/settings/product-lines" element={<ProductLinesPage />} />
        <Route path="/settings/fabric-settings" element={<FabricSettingsPage />} />
        <Route path="/settings/bin-types" element={<BinTypesPage />} />
        <Route path="/settings/audit-log" element={<AuditLogPage />} />
        <Route path="/settings/security" element={<SecurityPage />} />
        <Route path="/settings/workstation-steps" element={<WorkstationStepsPage />} />
        <Route path="/settings/parts-pull-template" element={<PartsPullTemplatePage />} />
        <Route path="/settings/operating-costs" element={<OperatingCostsPage />} />
        <Route path="/analytics/cost" element={<CostAnalyticsPage />} />
        <Route path="/production/floor" element={<ProductionFloorPage />} />
        <Route path="/backorders" element={<BackordersPage />} />
        <Route path="/support" element={<SupportTicketsPage />} />
        <Route path="/settings/sales-tax-nexus" element={<SalesTaxNexusPage />} />
        <Route path="/reports/tax-collected" element={<TaxCollectedReportPage />} />
        <Route path="/reports/tax-filings" element={<TaxFilingsPage />} />
        <Route path="/special-orders" element={<SpecialOrdersPage />} />
        <Route path="/warranty/registrations" element={<WarrantyRegistrationsPage />} />
        <Route path="/warranty/rma-queue" element={<RmaQueuePage />} />
        <Route path="/warranty/rma-history" element={<RmaHistoryPage />} />
        <Route path="/hr" element={<HRDashboardPage />} />
        <Route path="/hr/time" element={<TimeManagementPage />} />
        <Route path="/hr/crosschex" element={<CrosschexPage />} />
        <Route path="/hr/training" element={<TrainingManagementPage />} />
        <Route path="/hr/documents" element={<DocumentsManagementPage />} />
        <Route path="/hr/rewards" element={<RewardsManagementPage />} />
        <Route path="/hr/payroll" element={<PayrollPage />} />
        <Route path="/hr/discipline" element={<DisciplinePage />} />
        <Route path="/hr/separations" element={<SeparationsPage />} />
        <Route path="/hr/support-requests" element={<SupportRequestsPage />} />        <Route path="/hr/reviews" element={<ReviewsPage />} />
        <Route path="/settings/infraction-types" element={<InfractionTypesPage />} />
        <Route path="/calendar" element={<CompanyCalendarPage />} />
        {/* Accounting routes - consolidated into main layout */}
        <Route path="/accounting/dashboard" element={<AccountingDashboard />} />
        <Route path="/accounting/write-offs" element={<WriteOffsPage />} />
        <Route path="/accounting/approvals/dealers" element={<DealerApplicationsPage />} />
        <Route path="/accounting/approvals/dealers/:id" element={<DealerApplicationDetailPage />} />
        <Route path="/accounting/expenses/bills" element={<BillsListPage />} />
        <Route path="/accounting/expenses/bills/new" element={<BillEntryPage />} />
        <Route path="/accounting/expenses/bills/:id" element={<BillEntryPage />} />
        <Route path="/accounting/expenses/payroll" element={<Navigate to="/hr/payroll" replace />} />
        <Route path="/accounting/employees" element={<EmployeeTaxListPage />} />
        <Route path="/accounting/employees/:id" element={<EmployeeTaxEditPage />} />
        <Route path="/accounting/reports/payroll" element={<Navigate to="/reports/payroll" replace />} />
        <Route path="/accounting/revenue/shipped" element={<RevenueShippedPage />} />
        <Route path="/accounting/revenue/payments" element={<RevenuePaymentsPage />} />
        <Route path="/accounting/operating-costs" element={<OperatingCostsPage />} />
        <Route path="/accounting/quickbooks" element={<QuickBooksPage />} />
        <Route path="/accounting/payment-audit" element={<PaymentAuditPage />} />
        <Route path="/accounting/invoices-adjusted" element={<InvoicesAdjustedPage />} />
        <Route path="/accounting/credits" element={<CreditsPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
